import Component from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { throttle } from '@theme/utilities';

/**
 * A custom element that renders a paginated list with Physical DOM Virtualization for maximum Safari stability.
 */
export default class PaginatedList extends Component {
  constructor() {
    super();
    this.pagesContent = new Map();
    this.activePages = new Set();
    this.fetchingPages = new Set();
    this.infinityScrollObserver = null;
    this.resolveNextPagePromise = null;
    this.resolvePreviousPagePromise = null;
    this.isRenderingNext = false;
    this.isRenderingPrevious = false;
    this.aspectRatioHelper = null;
    this.firstLoadedPage = null;
    this.lastLoadedPage = null;
    this.preloadDistance = 1200;
    this.handleViewportProgress = throttle(() => this.checkViewportProgress(), 150);
    this.handleFilterUpdateBound = this.handleFilterUpdate.bind(this);
  }

  get sectionId() {
    return this.getAttribute('section-id') || '';
  }

  connectedCallback() {
    super.connectedCallback();
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard && templateCard instanceof HTMLElement) {
      this.aspectRatioHelper = new PaginatedListAspectRatioHelper({ templateCard });
    }

    this.initializePaginationState();
    this.fetchPage('next');
    this.fetchPage('previous');
    this.observeViewMore();
    window.addEventListener('scroll', this.handleViewportProgress, { passive: true });
    window.addEventListener('resize', this.handleViewportProgress);
    this.handleViewportProgress();

    document.addEventListener(ThemeEvents.FilterUpdate, this.handleFilterUpdateBound);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) this.infinityScrollObserver.disconnect();
    window.removeEventListener('scroll', this.handleViewportProgress);
    window.removeEventListener('resize', this.handleViewportProgress);
    this.handleViewportProgress.cancel?.();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.handleFilterUpdateBound);
  }

  refsUpdated() {
    this.observeViewMore();
    this.handleViewportProgress();
  }

  initializePaginationState() {
    const grid = this.refs.grid;
    if (!(grid instanceof HTMLElement)) return;

    const cards = Array.from(grid.querySelectorAll('li[data-page]'));
    if (cards.length === 0) {
      this.firstLoadedPage = null;
      this.lastLoadedPage = null;
      return;
    }

    const visiblePages = [...new Set(cards.map((card) => Number(card.getAttribute('data-page'))).filter(Number.isFinite))].sort(
      (a, b) => a - b
    );

    if (visiblePages.length === 0) {
      this.firstLoadedPage = null;
      this.lastLoadedPage = null;
      return;
    }

    for (const pageNumber of visiblePages) {
      this.ensurePageContainer(pageNumber);
      this.activePages.add(pageNumber);
      this.cachePageMarkupFromDOM(pageNumber);
    }

    this.firstLoadedPage = visiblePages[0];
    this.lastLoadedPage = visiblePages[visiblePages.length - 1];
  }

  observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;
    if (!viewMorePrevious && !viewMoreNext) return;

    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              if (entry.target === this.refs.viewMorePrevious) {
                this.renderPreviousPage();
              } else if (entry.target === this.refs.viewMoreNext) {
                this.renderNextPage();
              }
            }
          }
        },
        { rootMargin: `${this.preloadDistance}px 0px ${this.preloadDistance}px 0px` }
      );
    }

    if (viewMorePrevious instanceof Element) this.infinityScrollObserver.observe(viewMorePrevious);
    if (viewMoreNext instanceof Element) this.infinityScrollObserver.observe(viewMoreNext);
  }

  shouldUsePage(pageInfo) {
    if (!pageInfo) return false;
    const grid = this.refs.grid;
    if (!(grid instanceof HTMLElement)) return false;
    
    const lastPage = grid.dataset.lastPage;
    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;
    return true;
  }

  async fetchPage(type) {
    const page = this.getPageInfo(type);
    const resolvePromise = () => {
      if (type === 'next') {
        if (typeof this.resolveNextPagePromise === 'function') this.resolveNextPagePromise();
        this.resolveNextPagePromise = null;
      } else {
        if (typeof this.resolvePreviousPagePromise === 'function') this.resolvePreviousPagePromise();
        this.resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.shouldUsePage(page) || this.fetchingPages.has(page.page)) {
      resolvePromise();
      return;
    }

    this.fetchingPages.add(page.page);
    try {
      await this.fetchSpecificPage(page.page);
    } finally {
      this.fetchingPages.delete(page.page);
      resolvePromise();
    }
  }

  async fetchSpecificPage(pageNumber) {
    if (this.pagesContent.has(pageNumber)) return;

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('page', pageNumber.toString());
      url.hash = '';

      const pageHTML = await sectionRenderer.getSectionHTML(this.sectionId, true, url);
      if (pageHTML) {
        this.pagesContent.set(pageNumber, pageHTML);
      }
    } catch (e) {
      console.error('[PaginatedList] Fetch error', e);
    }
  }

  async renderPreviousPage() {
    if (this.isRenderingPrevious) return;
    const pageInfo = this.getPageInfo('previous');
    if (!pageInfo || !this.shouldUsePage(pageInfo)) return;

    this.isRenderingPrevious = true;
    try {
      const grid = this.refs.grid;
      if (!(grid instanceof HTMLElement)) return;

      if (!this.pagesContent.has(pageInfo.page)) {
        const promise = new Promise((res) => { this.resolvePreviousPagePromise = res; });
        this.fetchPage('previous');
        await promise;
      }

      const elements = this.getElementsFromPage(pageInfo.page);
      if (!elements) return;

      const container = this.ensurePageContainer(pageInfo.page, 'prepend');
      if (!(container instanceof HTMLElement)) return;

      if (this.getRenderedPageItems(container).length > 0) {
        this.registerLoadedPage(pageInfo.page);
        this.prunePages(pageInfo.page);
        return;
      }

      await this.batchAppendTo(container, Array.from(elements), 'prepend');
      this.registerLoadedPage(pageInfo.page);
      this.prunePages(pageInfo.page);
      requestAnimationFrame(() => this.handleViewportProgress());
    } finally {
      this.isRenderingPrevious = false;
    }
  }

  async renderNextPage() {
    if (this.isRenderingNext) return;
    const pageInfo = this.getPageInfo('next');
    if (!pageInfo || !this.shouldUsePage(pageInfo)) return;

    this.isRenderingNext = true;
    try {
      const grid = this.refs.grid;
      if (!(grid instanceof HTMLElement)) return;

      if (!this.pagesContent.has(pageInfo.page)) {
        const promise = new Promise((res) => { this.resolveNextPagePromise = res; });
        this.fetchPage('next');
        await promise;
      }

      const elements = this.getElementsFromPage(pageInfo.page);
      if (!elements) return;

      const container = this.ensurePageContainer(pageInfo.page, 'append');
      if (!(container instanceof HTMLElement)) return;

      if (this.getRenderedPageItems(container).length > 0) {
        this.registerLoadedPage(pageInfo.page);
        this.prunePages(pageInfo.page);
        return;
      }

      await this.batchAppendTo(container, Array.from(elements), 'append');
      this.registerLoadedPage(pageInfo.page);
      this.prunePages(pageInfo.page);
      
      setTimeout(() => {
        this.fetchPage('next');
        this.handleViewportProgress();
      }, 500);
    } finally {
      this.isRenderingNext = false;
    }
  }

  async batchAppendTo(container, elements, method = 'append') {
    const batchSize = 4;
    const scheduler = window.requestIdleCallback || ((cb) => setTimeout(cb, 30));
    const items = method === 'prepend' ? [...elements].reverse() : elements;
    
    for (let i = 0; i < items.length; i += batchSize) {
      const batch = items.slice(i, i + batchSize);
      await new Promise((resolve) => {
        scheduler(() => {
          if (method === 'prepend') {
            container.prepend(...batch.reverse());
          } else {
            container.append(...batch);
          }
          resolve(true);
        });
      });
    }
  }

  prunePages(currentPage) {
    const range = 2;
    const grid = this.refs.grid;
    if (!(grid instanceof HTMLElement)) return;

    this.activePages.forEach(page => {
      const container = grid.querySelector(`[data-page-container="${page}"]`);
      if (!(container instanceof HTMLElement)) return;

      const isDistant = Math.abs(page - currentPage) > range;
      const renderedItems = this.getRenderedPageItems(container);
      const placeholder = this.getPagePlaceholder(container);
      
      if (isDistant && renderedItems.length > 0) {
        const placeholderHeight = this.measurePageHeight(renderedItems);
        container.replaceChildren(this.createPagePlaceholder(page, placeholderHeight));
      } else if (!isDistant && placeholder instanceof HTMLElement) {
        this.restorePageFromCache(page, container);
      }
    });
  }

  getPageInfo(type) {
    if (this.firstLoadedPage === null || this.lastLoadedPage === null) {
      this.initializePaginationState();
    }

    if (this.firstLoadedPage === null || this.lastLoadedPage === null) {
      return { page: 1 };
    }

    return type === 'next' ? { page: this.lastLoadedPage + 1 } : { page: this.firstLoadedPage - 1 };
  }

  getElementsFromPage(pageNumber) {
    const html = this.pagesContent.get(pageNumber);
    if (!html) return null;
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return doc.querySelectorAll('.product-grid > *');
  }

  handleFilterUpdate() {
    this.pagesContent.clear();
    this.activePages.clear();
    this.fetchingPages.clear();
    this.firstLoadedPage = null;
    this.lastLoadedPage = null;
    const grid = this.refs.grid;
    if (grid instanceof HTMLElement) grid.innerHTML = '';
    this.fetchPage('next');
    this.handleViewportProgress();
  }

  ensurePageContainer(pageNumber, position = 'append') {
    const grid = this.refs.grid;
    if (!(grid instanceof HTMLElement)) return null;

    let container = grid.querySelector(`[data-page-container="${pageNumber}"]`);
    if (container instanceof HTMLElement) return container;

    container = document.createElement('div');
    container.style.display = 'contents';
    container.dataset.pageContainer = pageNumber.toString();

    const pageItems = Array.from(grid.children).filter((child) => {
      return (
        child instanceof HTMLElement &&
        child.matches('li[data-page]') &&
        Number(child.getAttribute('data-page')) === pageNumber
      );
    });

    if (pageItems.length > 0) {
      pageItems[0].before(container);
      container.append(...pageItems);
      return container;
    }

    if (position === 'prepend') {
      grid.prepend(container);
    } else {
      grid.append(container);
    }

    return container;
  }

  registerLoadedPage(pageNumber) {
    this.activePages.add(pageNumber);
    this.firstLoadedPage = this.firstLoadedPage === null ? pageNumber : Math.min(this.firstLoadedPage, pageNumber);
    this.lastLoadedPage = this.lastLoadedPage === null ? pageNumber : Math.max(this.lastLoadedPage, pageNumber);
  }

  cachePageMarkupFromDOM(pageNumber) {
    if (this.pagesContent.has(pageNumber)) return;

    const grid = this.refs.grid;
    if (!(grid instanceof HTMLElement)) return;

    const pageItems = Array.from(grid.querySelectorAll(`li[data-page="${pageNumber}"]`));
    if (pageItems.length === 0) return;

    const markup = pageItems.map((item) => item.outerHTML).join('');
    this.pagesContent.set(pageNumber, `<ul class="product-grid">${markup}</ul>`);
  }

  checkViewportProgress() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    this.restorePagesNearViewport();

    if (viewMoreNext instanceof HTMLElement && this.isSentinelNearViewport(viewMoreNext, 'next')) {
      this.renderNextPage();
    }

    if (viewMorePrevious instanceof HTMLElement && this.isSentinelNearViewport(viewMorePrevious, 'previous')) {
      this.renderPreviousPage();
    }
  }

  isSentinelNearViewport(element, type) {
    const rect = element.getBoundingClientRect();

    if (type === 'next') {
      return rect.top <= window.innerHeight + this.preloadDistance;
    }

    return rect.bottom >= -this.preloadDistance;
  }

  getRenderedPageItems(container) {
    return Array.from(container.querySelectorAll('li[data-page]'));
  }

  getPagePlaceholder(container) {
    const placeholder = container.querySelector('li[data-page-placeholder]');
    return placeholder instanceof HTMLElement ? placeholder : null;
  }

  measurePageHeight(items) {
    if (!items.length) return 0;

    const rects = items.map((item) => item.getBoundingClientRect());
    const top = Math.min(...rects.map((rect) => rect.top));
    const bottom = Math.max(...rects.map((rect) => rect.bottom));
    const height = Math.round(bottom - top);

    return Math.max(height, 1);
  }

  createPagePlaceholder(pageNumber, height) {
    const placeholder = document.createElement('li');
    placeholder.dataset.pagePlaceholder = pageNumber.toString();
    placeholder.setAttribute('aria-hidden', 'true');
    placeholder.className = 'product-grid__page-placeholder';
    placeholder.style.gridColumn = '1 / -1';
    placeholder.style.display = 'block';
    placeholder.style.height = `${height}px`;
    placeholder.style.minHeight = `${height}px`;
    placeholder.style.listStyle = 'none';
    placeholder.style.pointerEvents = 'none';
    placeholder.style.visibility = 'hidden';

    return placeholder;
  }

  restorePageFromCache(pageNumber, container) {
    const html = this.pagesContent.get(pageNumber);
    if (!html) return;

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const elements = Array.from(doc.querySelectorAll('.product-grid > *'));
    if (elements.length === 0) return;

    container.replaceChildren(...elements);
  }

  restorePagesNearViewport() {
    const grid = this.refs.grid;
    if (!(grid instanceof HTMLElement)) return;

    const pages = [...this.activePages].sort((a, b) => a - b);

    for (const pageNumber of pages) {
      const container = grid.querySelector(`[data-page-container="${pageNumber}"]`);
      if (!(container instanceof HTMLElement)) continue;

      const placeholder = this.getPagePlaceholder(container);
      if (!(placeholder instanceof HTMLElement)) continue;

      if (!this.isPlaceholderNearViewport(placeholder)) continue;

      this.restorePageFromCache(pageNumber, container);
    }
  }

  isPlaceholderNearViewport(element) {
    const rect = element.getBoundingClientRect();
    return rect.bottom >= -this.preloadDistance && rect.top <= window.innerHeight + this.preloadDistance;
  }
}

if (!customElements.get('paginated-list')) {
  customElements.define('paginated-list', PaginatedList);
}
